import { HighlightType } from '@node-space/ui-components'
import {
  LedgerWalletTransaction,
  TransactionStatus,
  TransactionStatuses,
} from 'types/ledger/ledgerWalletTransactions'

export const EARN_TRANSACTION_CATEGORY = {
  DEPOSIT: 'DEPOSIT',
  REWARD_PAYOUT: 'REWARD_PAYOUT',
  REWARD_WITHDRAWAL: 'REWARD_WITHDRAWAL',
} as const

export type EarnTransactionCategory =
  (typeof EARN_TRANSACTION_CATEGORY)[keyof typeof EARN_TRANSACTION_CATEGORY]

// Real ledger `type` values observed for rewards/earn wallets (see rewardsTransactions mock/handler)
const LEDGER_TYPE_TO_EARN_CATEGORY: Record<string, EarnTransactionCategory> = {
  rewardsDeposit: EARN_TRANSACTION_CATEGORY.DEPOSIT,
  rewardsYield: EARN_TRANSACTION_CATEGORY.REWARD_PAYOUT,
  rewardsEarned: EARN_TRANSACTION_CATEGORY.REWARD_PAYOUT,
  rewardsWithdrawal: EARN_TRANSACTION_CATEGORY.REWARD_WITHDRAWAL,
}

export const EARN_TRANSACTION_TYPE_LABEL_KEYS: Record<EarnTransactionCategory, string> = {
  [EARN_TRANSACTION_CATEGORY.DEPOSIT]: 'earn.transactionType.deposit',
  [EARN_TRANSACTION_CATEGORY.REWARD_PAYOUT]: 'earn.transactionType.rewardPayout',
  [EARN_TRANSACTION_CATEGORY.REWARD_WITHDRAWAL]: 'earn.transactionType.rewardWithdrawal',
}

export const getEarnTransactionCategory = (
  transaction: LedgerWalletTransaction
): EarnTransactionCategory | undefined => LEDGER_TYPE_TO_EARN_CATEGORY[transaction.type]

export const EARN_TRANSACTION_STATUS = {
  PENDING: 'PENDING',
  COMPLETE: 'COMPLETE',
  FAILED: 'FAILED',
} as const

export type EarnTransactionStatus =
  (typeof EARN_TRANSACTION_STATUS)[keyof typeof EARN_TRANSACTION_STATUS]

const FAILED_LEDGER_STATUSES: TransactionStatus[] = [
  TransactionStatuses.FAILED,
  TransactionStatuses.CANCELLED,
  TransactionStatuses.EXPIRED,
  TransactionStatuses.RETURNED,
]

export const getEarnTransactionStatus = (
  transaction: LedgerWalletTransaction
): EarnTransactionStatus => {
  if (transaction.status === TransactionStatuses.COMPLETED) {
    return EARN_TRANSACTION_STATUS.COMPLETE
  }
  if (FAILED_LEDGER_STATUSES.includes(transaction.status)) {
    return EARN_TRANSACTION_STATUS.FAILED
  }
  return EARN_TRANSACTION_STATUS.PENDING
}

export const EARN_TRANSACTION_STATUS_LABEL_KEYS: Record<EarnTransactionStatus, string> = {
  [EARN_TRANSACTION_STATUS.PENDING]: 'earn.transactionStatus.pending',
  [EARN_TRANSACTION_STATUS.COMPLETE]: 'earn.transactionStatus.complete',
  [EARN_TRANSACTION_STATUS.FAILED]: 'earn.transactionStatus.failed',
}

const EARN_TRANSACTION_STATUS_HIGHLIGHT_TYPES: Record<EarnTransactionStatus, HighlightType> = {
  [EARN_TRANSACTION_STATUS.PENDING]: HighlightType.Neutral,
  [EARN_TRANSACTION_STATUS.COMPLETE]: HighlightType.Success,
  [EARN_TRANSACTION_STATUS.FAILED]: HighlightType.Error,
}

export const getEarnTransactionStatusHighlightType = (
  transaction: LedgerWalletTransaction
): HighlightType => EARN_TRANSACTION_STATUS_HIGHLIGHT_TYPES[getEarnTransactionStatus(transaction)]
